// src/components/auth/Register.js
import React, { useState, useRef } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase/config';
import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail, updateProfile } from 'firebase/auth';
import { uploadProfileImage } from '../../services/storageService';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    // 파일 크기 검증 (100KB = 100 * 1024 bytes)
    if (file.size > 100 * 1024) {
      setError('이미지 크기는 100KB 이하여야 합니다.');
      fileInputRef.current.value = '';
      return;
    }

    // 이미지 파일 타입 검증
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드 가능합니다.');
      fileInputRef.current.value = '';
      return;
    }

    setImageFile(file);

    // 이미지 미리보기 생성
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      // Firebase Auth에서 이메일 중복 확인
      const signInMethods = await fetchSignInMethodsForEmail(auth, formData.email);
      if (signInMethods.length > 0) {
        throw new Error('이미 등록된 이메일입니다.');
      }

      // Firebase Auth에 사용자 등록
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;

      // 프로필 이미지가 있는 경우 업로드
      let photoURL = null;
      if (imageFile) {
        photoURL = await uploadProfileImage(user.uid, imageFile);
      }

      // 사용자 프로필 업데이트
      await updateProfile(user, {
        displayName: formData.name,
        photoURL: photoURL
      });

      // 회원가입 완료 후 index 페이지로 이동
      navigate('/');
    } catch (error) {
      setError(error.message || '회원가입 실패');
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5">
          회원가입
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            label="이메일"
            name="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="비밀번호"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            label="이름"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />
          <TextField
            margin="normal"
            fullWidth
            label="전화번호"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
          <div className="form-group">
            <label>프로필 이미지:</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              ref={fileInputRef}
            />
            <p className="file-info">* 100KB 이하의 이미지 파일만 업로드 가능합니다.</p>
          </div>
          {imagePreview && (
            <div className="image-preview">
              <img src={imagePreview} alt="프로필 미리보기" style={{maxWidth: '200px'}} />
            </div>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            회원가입
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default Register;